import {
    Autocomplete,
    Button,
    Card,
    FormControlLabel,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { formatPermissions, roleWithPermissions } from "./utils";
import { useFetch, useSnackbar } from "../../hooks";

import AddIcon from "@mui/icons-material/Add";
import { BACKEND_ROUTES } from "../../backendRoutes";
import { MODEL_PROPTYPES } from "../../models";
import PropTypes from "prop-types";
import { useState } from "react";

export function AddUserRoleForm({ scope, users, roles, onAdded }) {
    const { post } = useFetch();
    const { openSnackbar } = useSnackbar();

    const [user, setUser] = useState(null);
    const [role, setRole] = useState(null);
    const [autoGrantAccess, setAutoGrantAccess] = useState(false);
    const [cascadeToChildren, setCascadeToChildren] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleAdd = async () => {
        setIsSubmitting(true);
        // Parent roles are required, so missing ones are always created
        const result = await post(
            `${BACKEND_ROUTES.USER_ROLE}?cascadeParentCreate=true${cascadeToChildren ? "&cascadeChildCreate=true" : ""}`,
            {
                body: {
                    userUuid: user.uuid,
                    roleUuid: role.uuid,
                    [`${scope.type}Uuid`]: scope.uuid,
                    autoGrantAccess,
                },
            }
        );
        setIsSubmitting(false);
        if (result !== undefined) {
            openSnackbar("User added.", "success");
            setUser(null);
            setRole(null);
            setAutoGrantAccess(false);
            setCascadeToChildren(false);
            onAdded();
        }
    };

    return (
        <Card sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                Add a user
            </Typography>
            <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                flexWrap="wrap"
            >
                <Autocomplete
                    options={users}
                    getOptionLabel={(u) =>
                        `${u.firstName} ${u.lastName} (${u.email})`
                    }
                    isOptionEqualToValue={(o, v) => o.uuid === v.uuid}
                    value={user}
                    onChange={(_, v) => setUser(v)}
                    sx={{ minWidth: 300 }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Search and select a user"
                            size="small"
                        />
                    )}
                />
                <Autocomplete
                    options={roles}
                    getOptionLabel={(r) => r.name}
                    isOptionEqualToValue={(o, v) => o.uuid === v.uuid}
                    value={role}
                    onChange={(_, v) => setRole(v)}
                    sx={{ minWidth: 220 }}
                    renderOption={({ key, ...props }, r) => (
                        <li key={key} {...props}>
                            <Stack>
                                <Typography variant="body2">
                                    {r.name}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {formatPermissions(r.Permissions)}
                                </Typography>
                            </Stack>
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField {...params} label="Role" size="small" />
                    )}
                />
                <FormControlLabel
                    control={
                        <Switch
                            checked={autoGrantAccess}
                            onChange={(e) =>
                                setAutoGrantAccess(e.target.checked)
                            }
                            size="small"
                            color="primary"
                        />
                    }
                    label="Auto-grant"
                />
                {scope.type !== "site" && (
                    <FormControlLabel
                        control={
                            <Switch
                                checked={cascadeToChildren}
                                onChange={(e) =>
                                    setCascadeToChildren(e.target.checked)
                                }
                                size="small"
                                color="primary"
                            />
                        }
                        label={
                            scope.type === "company"
                                ? "Also grant on all contracts and sites"
                                : "Also grant on all sites"
                        }
                    />
                )}
            </Stack>
            <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                sx={{ mt: 2 }}
                onClick={handleAdd}
                disabled={!user || !role || isSubmitting}
            >
                Add
            </Button>
        </Card>
    );
}

AddUserRoleForm.propTypes = {
    scope: PropTypes.shape({
        type: PropTypes.oneOf(["company", "contract", "site"]).isRequired,
        uuid: PropTypes.string.isRequired,
    }).isRequired,
    users: PropTypes.arrayOf(PropTypes.shape(MODEL_PROPTYPES.User)).isRequired,
    roles: PropTypes.arrayOf(roleWithPermissions).isRequired,
    onAdded: PropTypes.func.isRequired,
};
